// src/lib/tryout/presetAssets.ts
/**
 * Reduces raw `tryout_preset_assets` DB rows into the in-memory PresetManifest
 * shape used by bakedEmptyUrl/bakedStagedUrl. Storage paths are resolved to
 * public URLs via the caller-supplied `toUrl`.
 */
import type { PresetManifest, PresetManifestEntry, StagingStyle } from './presetManifest';

export interface PresetAssetRow {
  preset_id: string;
  kind: 'empty' | 'staged';
  variant: 'A' | 'B';
  style: string;          // '' for empty, e.g. 'modern' for staged
  storage_path: string;
}

const roomFor = (presetId: string) => presetId.replace('-', '_');

export function rowsToManifest(rows: PresetAssetRow[], toUrl: (path: string) => string): PresetManifest {
  const m: PresetManifest = {};
  for (const r of rows) {
    const entry: PresetManifestEntry = m[r.preset_id] ?? { room: roomFor(r.preset_id), empty: { a: '', b: '' }, staged: {} };
    const slot = r.variant.toLowerCase() as 'a' | 'b';
    const url = toUrl(r.storage_path);
    if (r.kind === 'empty') {
      entry.empty[slot] = url;
    } else {
      const style = r.style as StagingStyle;
      entry.staged[style] = { ...(entry.staged[style] ?? {}), [slot]: url };
    }
    m[r.preset_id] = entry;
  }
  // Drop entries without both empties (a usable preset needs A and B).
  for (const id of Object.keys(m)) {
    if (!m[id].empty.a || !m[id].empty.b) delete m[id];
  }
  return m;
}
